/**
 * Authentication Debugging Utilities
 * 
 * This file contains utility functions for debugging authentication issues
 * in the browser console. They can be called directly in the console to
 * diagnose cookie, domain, and authentication state issues.
 */

// Import centralized environment detection
import { shouldShowDebugFeatures } from './environmentUtils';

// Simple environment detection (legacy - keeping for backward compatibility)
const detectEnvironment = () => {
  if (typeof window === 'undefined') return 'staging';
  const hostname = window.location.hostname;
  if (hostname === 'app.sequenceplanner.com' || hostname === 'sequenceplanner.com') {
    return 'production';
  }
  return 'staging';
};

// Maximum number of events to keep in history
const MAX_DEBUG_HISTORY = 50;

// Debug mode check - now respects production environment
export const isAuthDebugMode = (): boolean => {
  // In production, only enable through explicit query param
  if (detectEnvironment() === 'production') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('debug') && urlParams.get('debug') === 'auth';
  }
  
  // In non-production, enable through URL param or by default in certain pages
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('debug') && urlParams.get('debug') === 'auth';
};

// Log and store authentication events
export const logAuthEvent = (
  eventType: string, 
  details: Record<string, any>,
  isError = false
): void => {
  // Create timestamp
  const timestamp = new Date().toISOString();
  
  // Format event for logging
  const eventData = {
    timestamp,
    eventType,
    details,
    isError,
    url: window.location.href
  };
  
  // Only log to console in non-production or debug mode
  if (shouldShowDebugFeatures() || isAuthDebugMode()) {
    if (isError) {
      console.error(
        `%c🔑 AUTH DEBUG [${eventType}] %c${timestamp}`,
        'background: #FF5555; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
        'color: #FF5555; font-weight: normal;',
        details
      );
    } else {
      console.log(
        `%c🔑 AUTH DEBUG [${eventType}] %c${timestamp}`,
        'background: #4361ee; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
        'color: #4361ee; font-weight: normal;',
        details
      );
    }
  }
  
  // Store in localStorage if in debug mode
  if (isAuthDebugMode()) {
    storeDebugEvent(eventData);
  }
};

// Log auth-related localStorage state - internal implementation
const logStorageStateInternal = (context: string): Record<string, any> => {
  const storageItems: Record<string, any> = {};
  
  // Get all auth-related items from localStorage
  const authRelatedKeys = [
    'auth_token',
    'user',
    'auth_error',
    'expired_token',
    'auth_timestamp'
  ];
  
  authRelatedKeys.forEach(key => {
    const value = localStorage.getItem(key);
    storageItems[key] = value !== null ? (
      // Try to parse JSON if applicable
      key === 'user' && value ? JSON.parse(value) : value
    ) : null;
  });
  
  // Get cookie information
  const cookies = {
    auth_token: document.cookie.includes('auth_token'),
    auth_status: document.cookie.includes('auth_status=logged_in')
  };
  
  // Log storage state
  logAuthEvent('STORAGE_STATE', {
    context,
    localStorage: storageItems,
    cookies
  });
  
  return storageItems;
};

// Alias for backward compatibility - use logLocalStorageDetails moving forward
export const logStorageState = logLocalStorageDetails;

// Log token info without revealing full token
export const logTokenInfo = (token: string, reason: string): void => {
  if (!token) {
    logAuthEvent('TOKEN', { reason, token: null });
    return;
  }
  
  // Only log partial token for security
  const firstChars = token.substring(0, 6);
  const lastChars = token.substring(token.length - 4);
  const length = token.length;
  
  logAuthEvent('TOKEN', {
    reason,
    tokenPreview: `${firstChars}...${lastChars}`,
    tokenLength: length,
    tokenFirstChars: firstChars
  });
};

// Store debug event in localStorage
const storeDebugEvent = (eventData: any): void => {
  try {
    // Get existing history
    const historyJson = localStorage.getItem('auth_debug_history');
    let history = historyJson ? JSON.parse(historyJson) : [];
    
    // Add new event
    history.unshift(eventData);
    
    // Limit size
    if (history.length > MAX_DEBUG_HISTORY) {
      history = history.slice(0, MAX_DEBUG_HISTORY);
    }
    
    // Save history
    localStorage.setItem('auth_debug_history', JSON.stringify(history));
  } catch (err) {
    console.error('Failed to store debug event:', err);
  }
};

// Get debug history
export const getDebugHistory = (): any[] => {
  try {
    const historyJson = localStorage.getItem('auth_debug_history');
    return historyJson ? JSON.parse(historyJson) : [];
  } catch (err) {
    console.error('Failed to retrieve debug history:', err);
    return [];
  }
};

// Clear debug history
export const clearDebugHistory = (): void => {
  localStorage.removeItem('auth_debug_history');
};

export const logAuth = (level: 'info' | 'warn' | 'error', message: string, data?: any) => {
  const prefix = '[AUTH DEBUG]';
  
  // Store entry in debug history
  storeDebugEntry(level, message, data);
  
  switch (level) {
    case 'info':
      console.log(`${prefix} ${message}`, data);
      break;
    case 'warn':
      console.warn(`${prefix} ${message}`, data);
      break;
    case 'error':
      console.error(`${prefix} ${message}`, data);
      break;
  }
};

/**
 * Logs the current authentication state including cookies
 * Call this in the browser console: debugAuth()
 */
export function debugAuth() {
  console.group('%c Authentication Debugger', 'font-weight: bold; font-size: 16px;');

  // Check for cookies
  console.log('Checking cookies...');
  const cookies = document.cookie;
  console.log('Cookie string:', cookies);
  
  // Parse and examine cookies
  const cookieMap: Record<string, string> = {};
  cookies.split(';').forEach(cookie => {
    const parts = cookie.trim().split('=');
    if (parts.length >= 2) {
      const key = parts[0].trim();
      const value = parts.slice(1).join('=');
      cookieMap[key] = value;
    }
  });
  
  console.log('Parsed cookies:', cookieMap);
  console.log('Auth token present:', 'auth_token' in cookieMap);
  console.log('Auth status present:', 'auth_status' in cookieMap);
  
  // Check domain information
  const currentDomain = window.location.hostname;
  const port = window.location.port;
  const protocol = window.location.protocol;
  const fullOrigin = window.location.origin;
  
  console.log('Current domain:', currentDomain);
  console.log('Port:', port);
  console.log('Protocol:', protocol);
  console.log('Full origin:', fullOrigin);
  
  // Check path
  console.log('Current path:', window.location.pathname);
  
  // Check if we can set test cookies
  console.log('Testing cookie setting capability...');
  const testCookieName = 'auth_debug_test_' + Date.now();
  document.cookie = `${testCookieName}=test; path=/`;
  
  // Check if it was set
  const testCookieSet = document.cookie.includes(testCookieName);
  console.log('Test cookie set successfully:', testCookieSet);
  
  // Clean up
  document.cookie = `${testCookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  
  // Test an API call to the auth debug endpoint if available
  console.log('Testing connection to debug endpoint...');
  fetch('/api/auth/debug/cookies', {
    credentials: 'include'
  })
    .then(response => {
      console.log('Debug endpoint status:', response.status);
      return response.json();
    })
    .then(data => {
      console.log('Debug endpoint response:', data);
    })
    .catch(error => {
      console.error('Error calling debug endpoint:', error);
    });
  
  console.groupEnd();
  
  return 'Authentication debug complete. Check the console for results.';
}

// Expose debugAuth to the window object for easy console access
if (typeof window !== 'undefined') {
  (window as any).debugAuth = debugAuth;
}

/**
 * Logs the current state of localStorage including authentication and campaign data
 * Call this in the browser console: logStorage()
 */
export function logLocalStorageDetails(label = ''): void {
  try {
    console.group(`%c LocalStorage State ${label ? `(${label})` : ''}`, 'font-weight: bold;');
    
    // Check for authentication related items
    const authToken = localStorage.getItem('auth_token');
    const lastUserId = localStorage.getItem('last_user_id');
    const lastUserEmail = localStorage.getItem('last_user_email');
    
    console.log('auth_token present:', !!authToken);
    console.log('last_user_id:', lastUserId);
    console.log('last_user_email:', lastUserEmail);
    
    // Check for campaigns
    const campaigns = localStorage.getItem('campaigns');
    const parsedCampaigns = campaigns ? JSON.parse(campaigns) : [];
    console.log('Campaigns in localStorage:', parsedCampaigns.length);
    
    // Check sync status
    const syncStatus = localStorage.getItem('campaign_sync_status');
    const parsedSyncStatus = syncStatus ? JSON.parse(syncStatus) : {};
    console.log('Sync status in localStorage:', Object.keys(parsedSyncStatus).length, 'entries');
    
    // Count pending syncs
    const pendingSyncs = Object.entries(parsedSyncStatus).filter(([, status]) => status === 'pending').length;
    console.log('Pending syncs:', pendingSyncs);
    
    // Get debug history count
    const debugHistory = getDebugHistory();
    console.log('Debug history entries:', debugHistory.length);
    
    console.groupEnd();
  } catch (error) {
    console.error('Error logging storage state:', error);
  }
}

// Expose logStorage to the window object for easy console access
if (typeof window !== 'undefined') {
  (window as any).logStorage = logLocalStorageDetails;
}

// Store debug history
export const storeDebugEntry = (type: string, message: string, data?: any): void => {
  try {
    const historyJson = localStorage.getItem('auth_debug_history');
    const history = historyJson ? JSON.parse(historyJson) : [];
    
    history.push({
      timestamp: new Date().toISOString(),
      type,
      message,
      data
    });
    
    // Keep last 100 entries
    const trimmedHistory = history.slice(-100);
    localStorage.setItem('auth_debug_history', JSON.stringify(trimmedHistory));
  } catch (e) {
    console.error('Error storing debug entry:', e);
  }
};

export default {
  logAuth,
  debugAuth,
  logLocalStorageDetails,
  storeDebugEntry,
  getDebugHistory,
  clearDebugHistory
}; 